import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { MUTED, L2_ACCENT, NEUTRAL_SURFACE } from '../colorTokens';
import { ALERT_STATE_STYLES, CLAMP_INNER_SX, STATUS_COLORS } from './monitorConstants';
import { compareNewestFirst, filterGroupsByAlertState, notifiedTargetGroups, notifyGroupKey } from './monitorStats';

// Reference text for the "Type" info popup — every value DataDog's Monitors API can return.
const MONITOR_TYPE_REFERENCE = [
  ['audit alert', 'Audit Trail events matching a query'],
  ['ci-pipelines alert', 'CI pipeline execution results'],
  ['ci-tests alert', 'CI test run results'],
  ['composite', 'Boolean combination of other monitors'],
  ['cost alert', 'Cloud cost anomaly/threshold'],
  ['database-monitoring alert', 'DB query/performance metrics (DBM)'],
  ['error-tracking alert', 'Error Tracking issue occurrences'],
  ['event alert', 'Legacy event stream query'],
  ['event-v2 alert', 'Event stream matching a query'],
  ['log alert', 'Log search query'],
  ['metric alert', 'Classic metric/query-alert threshold'],
  ['network alert', 'Network performance monitoring'],
  ['process alert', 'Live Process monitoring query'],
  ['query alert', 'Metric query threshold'],
  ['rum alert', 'Real User Monitoring query'],
  ['service check', 'Agent/integration service check status'],
  ['slo alert', 'SLO burn rate/error budget'],
  ['synthetics alert', 'Synthetic test (API/browser) failures'],
  ['trace-analytics alert', 'APM span/trace analytics query']
];

// Fields in the raw-detail popup that render as links (http/https values only).
const DETAIL_LINK_KEYS = new Set(['url', 'runbookUrl']);

/** Human-readable rendering of one monitor field's value for the raw-detail popup. */
function formatDetailValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'object') return Object.keys(value).length ? JSON.stringify(value) : '—';
  return String(value);
}

const TAB_SX = { minHeight: 36, textTransform: 'none' };
const GROUP_HEADER_FOCUS_SX = { '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' } };

/** Set of expanded keys with a toggle -- backs the collapsible group rows in the popups. */
function useToggleSet() {
  const [set, setSet] = useState(() => new Set());
  const toggle = useCallback((key) => {
    setSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const clear = useCallback(() => setSet(new Set()), []);
  return { has: (key) => set.has(key), toggle, clear };
}

const MonitorLink = ({ monitor }) => (
  monitor.url ? <Link href={monitor.url} target="_blank" rel="noopener noreferrer">{monitor.name}</Link> : monitor.name
);

/** Shared dialog chrome: title + close icon, scrollable content, optional Close button. */
export const PopupDialog = ({ open, onClose, title, maxWidth = 'md', paperSx, showClose = true, children }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={maxWidth}
    fullWidth
    {...(paperSx && { PaperProps: { sx: paperSx } })}
  >
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      {title}
      <IconButton size="small" onClick={onClose} aria-label="Close">
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>{children}</DialogContent>
    {showClose && (
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    )}
  </Dialog>
);

/** Full-width shaded table row that toggles a group open/closed. */
const GroupHeaderRow = ({ colSpan, expanded, onToggle, ariaLabel, trailing, children }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ bgcolor: NEUTRAL_SURFACE, p: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <ButtonBase
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${ariaLabel}`}
        sx={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1,
          justifyContent: 'flex-start', textAlign: 'left', px: 2, py: 0.75,
          ...GROUP_HEADER_FOCUS_SX
        }}
      >
        {expanded ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
        {children}
      </ButtonBase>
      {trailing}
      </Box>
    </TableCell>
  </TableRow>
);

const GroupTitle = ({ children }) => (
  <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', overflowWrap: 'anywhere' }}>{children}</Typography>
);

const MONITOR_COLUMN = { label: 'Monitor', render: (m) => <MonitorLink monitor={m} /> };
const ACTIVE_COLUMN = {
  label: 'Active',
  render: (m) => (m.alertState === 'Active' ? 'Yes' : 'No'),
  sx: (m) => ({ color: (ALERT_STATE_STYLES[m.alertState] || { color: MUTED }).color, fontWeight: 700 })
};

/**
 * Popup of collapsible groups (header row per group, its monitors underneath when expanded) with
 * a row of tabs above -- shared by the Unique Creators and Unique Notify Targets popups.
 * `columns` are { label, width, render(monitor), sx?(monitor) }; `groupAriaLabel(group)` finishes
 * the "Expand/Collapse …" label of each header.
 */
const GroupedMonitorsPopup = ({
  open, onClose, title, tabs, tab, onTab, groups, columns, expanded, groupAriaLabel, maxWidth, renderGroupLink
}) => (
  <PopupDialog open={open} onClose={onClose} title={title} maxWidth={maxWidth}>
    <Tabs
      value={tab}
      onChange={(_, next) => onTab(next)}
      sx={{ minHeight: 36, mb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}
    >
      {tabs.map(({ value, label }) => <Tab key={value} value={value} label={label} sx={TAB_SX} />)}
    </Tabs>
    <TableContainer>
      <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            {columns.map((c) => <TableCell key={c.label} sx={{ width: c.width }}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => {
            const isOpen = expanded.has(group.key);
            return (
              <React.Fragment key={group.key}>
                <GroupHeaderRow
                  colSpan={columns.length}
                  expanded={isOpen}
                  onToggle={() => expanded.toggle(group.key)}
                  ariaLabel={groupAriaLabel(group)}
                  trailing={renderGroupLink?.(group)}
                >
                  <GroupTitle>{group.label} ({group.monitors.length})</GroupTitle>
                </GroupHeaderRow>
                {isOpen && group.monitors.map((m) => (
                  <TableRow key={m.id}>
                    {columns.map((c) => (
                      <TableCell key={c.label} sx={{ verticalAlign: 'top', overflowWrap: 'anywhere', ...c.sx?.(m) }}>
                        {c.render(m, group)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </PopupDialog>
);

const CREATOR_COLUMNS = [
  { ...MONITOR_COLUMN, width: '55%' },
  { label: 'Created', width: '28%', render: (m) => (m.created ? new Date(m.created).toLocaleString() : '—') },
  { ...ACTIVE_COLUMN, width: '17%' }
];

/** Unique Creators popup -- every creator with each monitor they created. */
export const CreatorsPopup = ({ open, onClose, monitors, creators, stats }) => {
  const [tab, setTab] = useState('All');
  const expanded = useToggleSet();
  const { clear } = expanded;
  // Each opening starts on the All tab with every creator collapsed.
  useEffect(() => {
    if (open) { setTab('All'); clear(); }
  }, [open, clear]);

  const groups = useMemo(() => filterGroupsByAlertState(creators, tab), [creators, tab]);
  const tabs = [
    { value: 'All', label: `All (${monitors.length})` },
    { value: 'Active', label: `Active (${stats.active})` },
    { value: 'Paused', label: `Paused (${stats.paused})` }
  ];
  return (
    <GroupedMonitorsPopup
      open={open} onClose={onClose} title={`Monitor creators (${groups.length})`}
      tabs={tabs} tab={tab} onTab={setTab} groups={groups} columns={CREATOR_COLUMNS} expanded={expanded}
      groupAriaLabel={(g) => `monitors created by ${g.label}`}
    />
  );
};

const RUNBOOK_COLUMNS = [
  { ...MONITOR_COLUMN, width: '55%' },
  { ...ACTIVE_COLUMN, width: '12%' },
  { label: 'Last Triggered', width: '33%', render: (m) => (m.lastTriggered ? new Date(m.lastTriggered).toLocaleString() : '—') }
];

/** Runbooks Defined popup -- each distinct runbook with every monitor tied to it. */
export const RunbooksPopup = ({ open, onClose, runbooks }) => {
  const [tab, setTab] = useState('All');
  const expanded = useToggleSet();
  const { clear } = expanded;
  useEffect(() => {
    if (open) { setTab('All'); clear(); }
  }, [open, clear]);

  const groups = useMemo(() => filterGroupsByAlertState(runbooks, tab), [runbooks, tab]);
  const tabs = useMemo(() => {
    const all = runbooks.flatMap((g) => g.monitors);
    const count = (state) => all.filter((m) => m.alertState === state).length;
    return [
      { value: 'All', label: `All (${all.length})` },
      { value: 'Active', label: `Active (${count('Active')})` },
      { value: 'Paused', label: `Paused (${count('Paused')})` }
    ];
  }, [runbooks]);
  return (
    <GroupedMonitorsPopup
      open={open} onClose={onClose} title={`Runbooks (${groups.length})`} maxWidth="lg"
      tabs={tabs} tab={tab} onTab={setTab} groups={groups} columns={RUNBOOK_COLUMNS} expanded={expanded}
      groupAriaLabel={(g) => `monitors tied to runbook ${g.label}`}
      renderGroupLink={(g) => (/^https?:\/\//i.test(g.label) && (
        <Link
          href={g.label} target="_blank" rel="noopener noreferrer"
          aria-label="Open runbook in a new tab" sx={{ display: 'flex', px: 2 }}
        >
          <OpenInNewIcon sx={{ fontSize: 16 }} />
        </Link>
      ))}
    />
  );
};

const NOTIFY_COLUMNS = [
  { ...MONITOR_COLUMN, width: '80%' },
  { ...ACTIVE_COLUMN, width: '20%' }
];

/** Unique Notify Targets popup -- each distinct target grouping (or each target individually) with its monitors. */
export const NotifyGroupsPopup = ({ open, onClose, notifyGroups, individualTargets, stats }) => {
  const [tab, setTab] = useState('All');
  const expanded = useToggleSet();
  const { clear } = expanded;
  useEffect(() => {
    if (open) { setTab('All'); clear(); }
  }, [open, clear]);

  const groups = useMemo(
    () => (tab === 'Individual' ? individualTargets : filterGroupsByAlertState(notifyGroups, tab)),
    [notifyGroups, individualTargets, tab]
  );
  const tabs = [
    { value: 'All', label: `All (${stats.active + stats.paused})` },
    { value: 'Active', label: `Active = Yes (${stats.active})` },
    { value: 'Paused', label: `Active = No (${stats.paused})` },
    { value: 'Individual', label: `Individual (${individualTargets.length})` }
  ];
  return (
    <GroupedMonitorsPopup
      open={open} onClose={onClose} title={`Unique notify targets (${groups.length})`}
      tabs={tabs} tab={tab} onTab={setTab} groups={groups} columns={NOTIFY_COLUMNS} expanded={expanded}
      groupAriaLabel={(g) => `notify targets ${g.label}`}
    />
  );
};

const lastTriggeredText = (m) => (m.lastTriggered ? new Date(m.lastTriggered).toLocaleString() : '—');
const RECENT_NOTIFIED_COLUMNS = [
  { ...MONITOR_COLUMN, width: '60%' },
  { label: 'Last Triggered', width: '40%', render: lastTriggeredText }
];
// Used by the All and Shared-and-unique tabs; Unique-only monitors are always solo, so it adds nothing there.
const NOTIFY_DETAIL_COLUMNS = [
  { ...MONITOR_COLUMN, width: '45%' },
  {
    label: 'Notify', width: '25%',
    render: (m, group) => {
      const others = [...new Set(m.notifyTargets || [])].filter((t) => t !== group.key);
      return others.length === 0 ? 'Unique' : `Shared with ${others.join(', ')}`;
    }
  },
  { label: 'Last Triggered', width: '30%', render: lastTriggeredText }
];

// Same order as the monitor table's Recently Notified sort: most recent first, notify-target set,
// alert state, status, created (newest first), name.
function compareRecentlyNotified(a, b) {
  const ms = (v) => (v ? new Date(v).getTime() : -Infinity);
  const text = (v) => v || '';
  return compareNewestFirst(ms(a.lastTriggered), ms(b.lastTriggered))
    || notifyGroupKey(a).localeCompare(notifyGroupKey(b))
    || text(a.alertState).localeCompare(text(b.alertState))
    || text(a.status).localeCompare(text(b.status))
    || compareNewestFirst(ms(a.created), ms(b.created))
    || text(a.name).localeCompare(text(b.name));
}

/** Recently Notified donut popup -- each notify target that fired in `range`, with the monitors it was a target of. */
export const RecentlyNotifiedPopup = ({ open, onClose, monitors, range }) => {
  const [tab, setTab] = useState('All');
  const expanded = useToggleSet();
  const { clear } = expanded;
  useEffect(() => {
    if (open) { setTab('All'); clear(); }
  }, [open, clear]);

  const targets = useMemo(
    () => (range ? notifiedTargetGroups(monitors, range) : [])
      .map((t) => ({ ...t, monitors: [...t.monitors].sort(compareRecentlyNotified) })),
    [monitors, range]
  );
  const byTab = {
    All: targets,
    SharedAndUnique: targets.filter((t) => t.kind === 'sharedAndUnique'),
    SharedOnly: targets.filter((t) => t.kind === 'sharedOnly'),
    UniqueOnly: targets.filter((t) => t.kind === 'uniqueOnly')
  };
  const tabs = [
    { value: 'All', label: `All (${byTab.All.length})` },
    { value: 'SharedAndUnique', label: `Shared and unique (${byTab.SharedAndUnique.length})` },
    { value: 'SharedOnly', label: `Shared only (${byTab.SharedOnly.length})` },
    { value: 'UniqueOnly', label: `Unique target only (${byTab.UniqueOnly.length})` }
  ];
  return (
    <GroupedMonitorsPopup
      open={open} onClose={onClose} title={`Recently notified targets: ${range?.title ?? ''}`}
      tabs={tabs} tab={tab} onTab={setTab} groups={byTab[tab]} expanded={expanded}
      columns={tab === 'UniqueOnly' ? RECENT_NOTIFIED_COLUMNS : NOTIFY_DETAIL_COLUMNS}
      groupAriaLabel={(g) => `monitors notifying ${g.label}`}
    />
  );
};

/** Duplicates donut popup -- every overlap group (any size), each collapsed to its most-recently-triggered monitor until expanded. */
export const DuplicateGroupsPopup = ({ open, onClose, groups }) => {
  const expanded = useToggleSet();
  return (
    <PopupDialog
      open={open} onClose={onClose} title={`Duplicate monitor groups (${groups.length})`}
      maxWidth={false} paperSx={{ maxWidth: '95vw' }}
    >
      {groups.length === 0 ? (
        <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>No overlapping monitors found.</Typography>
      ) : (
        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                {['Name', 'Creator', 'Notify Targets', 'Differences'].map((label) => (
                  <TableCell key={label} sx={{ width: '25%' }}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => {
                const isOpen = expanded.has(group.groupId);
                const visibleMonitors = isOpen ? group.monitors : group.monitors.slice(0, 1);
                const innerSx = isOpen ? { overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' } : CLAMP_INNER_SX;
                return (
                  <React.Fragment key={`group-${group.groupId}`}>
                    <GroupHeaderRow
                      colSpan={4}
                      expanded={isOpen}
                      onToggle={() => expanded.toggle(group.groupId)}
                      ariaLabel={`overlap group ${group.groupId}`}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>
                        Group #{group.groupId} ({group.monitors.length} monitors)
                      </Typography>
                      <Typography sx={{ color: MUTED, fontSize: '0.8125rem' }}>
                        — Shared: {group.similarities.summary}
                      </Typography>
                    </GroupHeaderRow>
                    {visibleMonitors.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell sx={{ verticalAlign: 'top' }}><Box sx={innerSx}><MonitorLink monitor={m} /></Box></TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}><Box sx={innerSx}>{m.creator}</Box></TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}><Box sx={innerSx}>{(m.notifyTargets || []).join(', ')}</Box></TableCell>
                        <TableCell sx={{ verticalAlign: 'top', color: L2_ACCENT }}><Box sx={innerSx}>{m.differences?.summary}</Box></TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </PopupDialog>
  );
};

/** Detail popup for one overlap group (opened from the Duplicate column chip). */
export const OverlapGroupPopup = ({ group, onClose }) => {
  if (!group) return null;
  return (
    <PopupDialog
      open onClose={onClose} maxWidth="lg"
      title={`Overlap group #${group.groupId} (${group.monitors.length} monitors)`}
    >
      <Typography sx={{ fontSize: '0.875rem', mb: 2 }}>
        <strong>Shared:</strong> {group.similarities.summary}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Status', 'Alert State', 'Creator', 'Differences'].map((label) => <TableCell key={label}>{label}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {group.monitors.map((m) => (
              <TableRow key={m.id}>
                <TableCell><MonitorLink monitor={m} /></TableCell>
                <TableCell sx={{ color: STATUS_COLORS[m.status] || MUTED, fontWeight: 700 }}>{m.status}</TableCell>
                <TableCell>{m.alertState}</TableCell>
                <TableCell>{m.creator}</TableCell>
                <TableCell sx={{ color: L2_ACCENT }}>{m.differences?.summary}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </PopupDialog>
  );
};

/** Raw monitor detail popup -- every cached key/value pair for the clicked row. */
export const MonitorDetailPopup = ({ monitor, onClose }) => {
  if (!monitor) return null;
  return (
    <PopupDialog open onClose={onClose} title={monitor.name || `Monitor #${monitor.id}`}>
      <Table size="small">
        <TableBody>
          {Object.entries(monitor).map(([key, value]) => (
            <TableRow key={key}>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top', width: '16ch' }}>{key}</TableCell>
              <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {DETAIL_LINK_KEYS.has(key) && /^https?:\/\//i.test(String(value || ''))
                  ? <Link href={value} target="_blank" rel="noopener noreferrer">{value}</Link>
                  : formatDetailValue(value)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </PopupDialog>
  );
};

/** Reference table for the Type column's info icon. */
export const TypeReferencePopup = ({ open, onClose }) => (
  <PopupDialog open={open} onClose={onClose} title="Monitor type reference" maxWidth="sm" showClose={false}>
    <Table size="small">
      <TableBody>
        {MONITOR_TYPE_REFERENCE.map(([type, desc]) => (
          <TableRow key={type}>
            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{type}</TableCell>
            <TableCell sx={{ color: MUTED }}>{desc}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </PopupDialog>
);
